import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  AppBar,
  Toolbar,
  Chip,
  Button,
  Stack,
  Grid,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  Tooltip,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import theme from "./theme";
import SimulationOverlay from "./components/SimulationOverlay";
import DroneGrid from "./components/DroneGrid";
import Legend from "./components/Legend";
import SimulationView3D from "./components/SimulationView3D";
import ScenarioSetup from "./components/ScenarioSetup";
import ForecastPanel from "./components/ForecastPanel";
import ConditionsCard from "./components/ConditionsCard";
import ImpactCard from "./components/ImpactCard";
import PlaybackBar from "./components/PlaybackBar";
import ResponsePanel from "./components/ResponsePanel";
import StrategyLab from "./components/StrategyLab";
import { FirefightingToolbar, TOOLS } from "./components/FirefightingToolbar";
import { loadScenario, configFromPreset, PRESETS } from "./services/scenarioLoader";
import { FireEngine } from "./simulation/fireEngine";
import { updateDroneMissions, DRONE_PLANNING_INTERVAL_HRS } from "./simulation/dronePlanner";
import { runDamageForecast } from "./simulation/damageForecast";
import { GRID_SIZE_PX } from "./simulation/constants";

const BASE_DRONE_SPACING = 50;
const SIM_TICK_MS = 100;

// Realistic tool constraints (units available / turnaround time)
const MAX_CONCURRENT_USES = {
  [TOOLS.WATER_DROP]: 2,
  [TOOLS.RETARDANT_DROP]: 3,
  [TOOLS.BACKFIRE]: 2,
  [TOOLS.DIG_BREAK]: 5,
  [TOOLS.NONE]: Infinity,
};
const TOOL_COOLDOWN_SIM_HOURS = {
  [TOOLS.WATER_DROP]: 0.5,
  [TOOLS.RETARDANT_DROP]: 1,
  [TOOLS.BACKFIRE]: 4,
  [TOOLS.DIG_BREAK]: 1,
  [TOOLS.NONE]: 0,
};

function App() {
  // --- Scenario state ---
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadStatus, setLoadStatus] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);

  // --- Simulation state ---
  const [simulationStatus, setSimulationStatus] = useState("idle");
  const [currentSimTime, setCurrentSimTime] = useState(null);
  const [renderData, setRenderData] = useState(null);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [infraStats, setInfraStats] = useState({ initial: 0, current: 0 });
  const [statsHistory, setStatsHistory] = useState([]);
  const [speed, setSpeed] = useState(1); // sim hours per tick
  const engineRef = useRef(null);
  const simTimeRef = useRef(0);
  const speedRef = useRef(1);
  speedRef.current = speed;

  // --- Interaction state ---
  const [selectedTool, setSelectedTool] = useState(TOOLS.NONE);
  const [activeCooldowns, setActiveCooldowns] = useState({});
  const [toolEvents, setToolEvents] = useState([]);
  const simAreaRef = useRef(null);

  // --- Drone state ---
  const [droneModeActive, setDroneModeActive] = useState(false);
  const [droneDensity, setDroneDensity] = useState(5);
  const [droneStrategyId, setDroneStrategyId] = useState("nearest");
  const [droneStations, setDroneStations] = useState([]);
  const [activeMissions, setActiveMissions] = useState([]);
  const missionsRef = useRef([]);
  const lastPlanTimeRef = useRef(0);
  const droneStrategyRef = useRef("nearest");
  droneStrategyRef.current = droneStrategyId;

  // --- View state ---
  const [viewMode, setViewMode] = useState("2D");
  const [showBurnMap, setShowBurnMap] = useState(false);
  const [burnMapOpacity, setBurnMapOpacity] = useState(0.5);
  const [sidebarTab, setSidebarTab] = useState(0);

  // --- Forecast state ---
  const [forecast, setForecast] = useState(null);
  const [forecastRunning, setForecastRunning] = useState(false);
  const [forecastProgress, setForecastProgress] = useState(0);
  const [forecastVisible, setForecastVisible] = useState(true);
  const [forecastHorizon, setForecastHorizon] = useState(48);

  const dims = scenario?.dims || { rows: 0, cols: 0 };

  // --- Scenario loading (progressive) ------------------------------------
  const simStartedRef = useRef(false);
  const pendingScenarioRef = useRef(null);

  const applyScenario = useCallback((sc) => {
    const engine = new FireEngine(sc);
    engineRef.current = engine;
    simTimeRef.current = sc.startTime.getTime();
    missionsRef.current = [];
    lastPlanTimeRef.current = 0;
    setScenario(sc);
    setCurrentSimTime(new Date(sc.startTime));
    setCurrentWeather(sc.weather.at(sc.startTime.getTime()));
    setRenderData(engine.getRenderState());
    setInfraStats(engine.stats());
    setStatsHistory([]);
    setActiveMissions([]);
    setToolEvents([]);
  }, []);

  const handleLoadScenario = useCallback(
    async (config) => {
      setLoading(true);
      setLoadError(null);
      setSimulationStatus("idle");
      setForecast(null);
      simStartedRef.current = false;
      setActiveCooldowns({});
      setSelectedTool(TOOLS.NONE);
      setShowBurnMap(false);
      try {
        const sc = await loadScenario(
          config,
          (msg) => setLoadStatus(msg),
          (partial) => {
            if (!simStartedRef.current) applyScenario(partial);
          }
        );
        if (!simStartedRef.current) applyScenario(sc);
        else pendingScenarioRef.current = sc; // applied on next Reset
      } catch (err) {
        console.error("Scenario load failed:", err);
        setLoadError(err.message || String(err));
      } finally {
        setLoading(false);
      }
    },
    [applyScenario]
  );

  // Auto-load the default preset once (ref guard for StrictMode)
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    handleLoadScenario(configFromPreset(PRESETS[0]));
  }, [handleLoadScenario]);

  // --- Drone stations from grid + density ---------------------------------
  useEffect(() => {
    if (!scenario) return;
    const { rows, cols } = scenario.dims;
    const spacing = Math.max(1, Math.round(BASE_DRONE_SPACING / droneDensity));
    const offset = Math.floor(spacing / 2);
    const stations = [];
    for (let r = offset; r < rows; r += spacing) {
      for (let c = offset; c < cols; c += spacing) {
        stations.push({ row: r, col: c });
      }
    }
    setDroneStations(stations);
  }, [scenario, droneDensity]);

  // --- Simulation loop ----------------------------------------------------
  useEffect(() => {
    if (simulationStatus !== "running" || !scenario || !engineRef.current)
      return undefined;

    const endMs = scenario.endTime.getTime();
    const id = setInterval(() => {
      const engine = engineRef.current;
      const hours = speedRef.current;
      let t = simTimeRef.current;

      for (let h = 0; h < hours && t < endMs; h++) {
        t += 3600 * 1000;
        const weather = scenario.weather.at(t);

        if (droneModeActive && droneStations.length > 0) {
          if (t >= lastPlanTimeRef.current + DRONE_PLANNING_INTERVAL_HRS * 3600 * 1000) {
            lastPlanTimeRef.current = t;
            const { missions, drops } = updateDroneMissions({
              engine,
              stations: droneStations,
              missions: missionsRef.current,
              nowMs: t,
              structureValue: scenario.structureValue,
              weather,
              strategyId: droneStrategyRef.current,
            });
            missionsRef.current = missions;
            for (const d of drops) {
              engine.applyTool("RETARDANT_DROP", d.row, d.col, weather);
            }
          }
        }
        engine.step(weather, 1);
      }

      const weatherNow = scenario.weather.at(t);
      simTimeRef.current = t;
      setCurrentSimTime(new Date(t));
      setCurrentWeather(weatherNow);
      setRenderData(engine.getRenderState());
      const stats = engine.stats();
      setInfraStats(stats);
      setStatsHistory((prev) => {
        const next = [...prev, { tMs: t, burntTiles: stats.totalBurntTiles }];
        // Decimate to keep the sparkline light
        return next.length > 400 ? next.filter((_, i) => i % 2 === 0) : next;
      });
      setActiveMissions(missionsRef.current.filter((m) => m.status === "deploying"));

      if (t >= endMs) setSimulationStatus("completed");
    }, SIM_TICK_MS);

    return () => clearInterval(id);
  }, [simulationStatus, scenario, droneModeActive, droneStations]);

  // Clear missions when sim stops
  useEffect(() => {
    if (simulationStatus !== "running" && missionsRef.current.length > 0) {
      missionsRef.current = [];
      setActiveMissions([]);
    }
  }, [simulationStatus]);

  // --- Control handlers ---------------------------------------------------
  const handleStart = () => {
    if (scenario) {
      simStartedRef.current = true;
      setSimulationStatus("running");
    }
  };
  const handlePause = () => setSimulationStatus("paused");
  const handleReset = () => {
    if (!scenario) return;
    simStartedRef.current = false;
    setSimulationStatus("idle");
    if (pendingScenarioRef.current) {
      const sc = pendingScenarioRef.current;
      pendingScenarioRef.current = null;
      applyScenario(sc);
    } else {
      applyScenario(scenario);
    }
    setActiveCooldowns({});
    setSelectedTool(TOOLS.NONE);
    setForecast(null);
  };

  const handleSelectTool = (tool) => {
    setSelectedTool(tool === selectedTool ? TOOLS.NONE : tool);
  };

  const handleMapContainerClick = (event) => {
    if (viewMode === "3D" || droneModeActive) return;
    if (!selectedTool || selectedTool === TOOLS.NONE) return;
    if (!engineRef.current || !simAreaRef.current || !scenario) return;

    const currentSimTimeMs = simTimeRef.current;
    const cooldowns = (activeCooldowns[selectedTool] || []).filter(
      (endTime) => endTime > currentSimTimeMs
    );
    const maxUnits = MAX_CONCURRENT_USES[selectedTool] || 1;
    if (cooldowns.length >= maxUnits) return;

    const rect = simAreaRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return;
    const col = Math.floor((x / rect.width) * dims.cols);
    const row = Math.floor((y / rect.height) * dims.rows);

    const affected = engineRef.current.applyTool(selectedTool, row, col, currentWeather);
    if (affected.length === 0 && selectedTool !== TOOLS.WATER_DROP) return;

    setRenderData(engineRef.current.getRenderState());
    setInfraStats(engineRef.current.stats());
    setToolEvents((prev) => [
      ...prev.filter((e) => Date.now() - e.startTime < e.duration),
      { key: `${row},${col}-${Date.now()}`, r: row, c: col, tool: selectedTool, startTime: Date.now(), duration: 500 },
    ]);

    const cooldownMs = (TOOL_COOLDOWN_SIM_HOURS[selectedTool] || 0) * 3600000;
    if (cooldownMs > 0) {
      setActiveCooldowns((prev) => ({
        ...prev,
        [selectedTool]: [...cooldowns, currentSimTimeMs + cooldownMs],
      }));
    }
  };

  // --- Forecast -----------------------------------------------------------
  const handleRunForecast = async () => {
    if (!engineRef.current || !scenario || forecastRunning) return;
    setForecastRunning(true);
    setForecastProgress(0);
    if (simulationStatus === "running") setSimulationStatus("paused");
    try {
      const result = await runDamageForecast({
        engine: engineRef.current,
        weatherTimeline: scenario.weather,
        startMs: simTimeRef.current,
        horizonHours: forecastHorizon,
        runs: 12,
        onProgress: (p) => setForecastProgress(p),
      });
      setForecast(result);
      setForecastVisible(true);
    } catch (err) {
      console.error("Forecast failed:", err);
    } finally {
      setForecastRunning(false);
    }
  };

  // --- Strategy lab -> live sim -------------------------------------------
  const handleApplyStrategy = ({ strategyId, density }) => {
    setDroneStrategyId(strategyId);
    if (density > 0) setDroneDensity(density);
    setDroneModeActive(true);
    setSelectedTool(TOOLS.NONE);
    setSidebarTab(0);
  };

  const handleDroneModeChange = (checked) => {
    setDroneModeActive(checked);
    if (checked) setSelectedTool(TOOLS.NONE);
  };

  const aspectPct = dims.cols > 0 ? (dims.rows / dims.cols) * 100 : 75;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="sticky">
        <Toolbar variant="dense" sx={{ gap: 1.5, flexWrap: "wrap", py: 0.5 }}>
          <LocalFireDepartmentRoundedIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" sx={{ mr: 1 }}>
            Wildfire Sentinel
          </Typography>
          {scenario && (
            <Chip size="small" variant="outlined" color="primary" label={scenario.name} />
          )}
          {scenario && (
            <Tooltip
              title={`Terrain: ${scenario.sources.terrain} · Weather: ${scenario.sources.weather} · Infrastructure: ${scenario.sources.infrastructure}`}
            >
              <Chip
                size="small"
                variant="outlined"
                label={`${(scenario.totalStructures || 0).toLocaleString()} structures · live data`}
              />
            </Tooltip>
          )}
          {loading && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<CircularProgress size={12} color="warning" />}
              label={loadStatus || "Streaming data…"}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneRoundedIcon />}
            onClick={() => setScenarioDialogOpen(true)}
          >
            Scenario
          </Button>
        </Toolbar>
      </AppBar>

      <ScenarioSetup
        open={scenarioDialogOpen}
        onClose={() => setScenarioDialogOpen(false)}
        onLoad={handleLoadScenario}
        loading={loading}
        loadStatus={loadStatus}
        loadError={loadError}
      />

      <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1700, mx: "auto" }}>
        <Grid container spacing={2}>
          {/* ---------------- Map column ---------------- */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }} useFlexGap>
                <Typography variant="subtitle2" color="text.secondary">
                  Operational Map
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {scenario?.burnMapUrl && viewMode === "2D" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label="Actual burn map"
                      color={showBurnMap ? "warning" : "default"}
                      variant={showBurnMap ? "filled" : "outlined"}
                      onClick={() => setShowBurnMap(!showBurnMap)}
                    />
                    {showBurnMap && (
                      <Slider
                        size="small"
                        value={burnMapOpacity}
                        onChange={(_, v) => setBurnMapOpacity(v)}
                        min={0}
                        max={1}
                        step={0.05}
                        sx={{ width: 70 }}
                      />
                    )}
                  </Stack>
                )}
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={viewMode}
                  onChange={(_, v) => v && setViewMode(v)}
                >
                  <ToggleButton value="2D">
                    <MapRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} /> 2D
                  </ToggleButton>
                  <ToggleButton value="3D">
                    <ViewInArRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} /> 3D
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "#05080c",
                  cursor:
                    viewMode === "2D" && selectedTool !== TOOLS.NONE && !droneModeActive
                      ? "crosshair"
                      : "default",
                }}
                onClick={handleMapContainerClick}
              >
                {!scenario ? (
                  <Box sx={{ textAlign: "center", p: 8 }}>
                    <CircularProgress color="primary" />
                    <Typography sx={{ mt: 2 }} color="text.secondary">
                      {loading
                        ? loadStatus || "Loading scenario data…"
                        : loadError
                        ? "Scenario failed to load — open Scenario to retry."
                        : "Open Scenario to begin."}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: "relative", width: "100%", height: 0, paddingTop: `${aspectPct}%` }}>
                    <Box
                      ref={simAreaRef}
                      sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                    >
                      <SimulationOverlay
                        gridDimensions={dims}
                        renderData={renderData}
                        baseTypeGrid={scenario.initialGridType}
                        renderElevation={scenario.renderElevation}
                        shade={scenario.shade}
                        droneStations={droneStations}
                        activeMissions={activeMissions}
                        droneModeActive={droneModeActive}
                        weather={currentWeather}
                        forecast={forecast}
                        forecastVisible={forecastVisible}
                        toolEvents={toolEvents}
                        zIndexValue={viewMode === "2D" ? 3 : -1}
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          justifyContent: "space-between",
                          alignItems: { xs: "stretch", sm: "flex-start" },
                          p: { xs: 0.5, sm: 1 },
                          pointerEvents: "none",
                          boxSizing: "border-box",
                          zIndex: 9,
                        }}
                      >
                        {viewMode === "2D" && (
                          <FirefightingToolbar
                            selectedTool={selectedTool}
                            onSelectTool={handleSelectTool}
                            disabled={droneModeActive}
                            sx={{ pointerEvents: "auto", mb: { xs: 1, sm: 0 } }}
                          />
                        )}
                        <Legend
                          sx={{ pointerEvents: "auto", marginLeft: viewMode === "3D" ? "auto" : 0 }}
                        />
                      </Box>

                      {viewMode === "2D" && (
                        <DroneGrid
                          gridDimensions={{
                            ...dims,
                            width: dims.cols * GRID_SIZE_PX,
                            height: dims.rows * GRID_SIZE_PX,
                          }}
                          zIndexValue={8}
                          droneModeActive={droneModeActive}
                          simulationTime={currentSimTime?.getTime() || 0}
                          droneStations={droneStations}
                          activeMissions={activeMissions}
                        />
                      )}

                      {viewMode === "3D" && renderData && (
                        <SimulationView3D
                          gridState={renderData.grid}
                          elevationGrid={scenario.renderElevation}
                          gridDimensions={dims}
                          persistentToolMarkers={renderData.markers}
                          droneStations={droneModeActive ? droneStations : []}
                          activeMissions={activeMissions}
                          zIndexValue={3}
                          currentSimTime={currentSimTime}
                          weather={currentWeather}
                        />
                      )}

                      {loading && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: "50%",
                            transform: "translateX(-50%)",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            zIndex: 10,
                            pointerEvents: "none",
                          }}
                        >
                          <CircularProgress size={14} color="warning" />
                          <Typography variant="caption" color="text.secondary">
                            {loadStatus || "Loading remaining data…"}
                          </Typography>
                        </Box>
                      )}

                      {showBurnMap && scenario.burnMapUrl && (
                        <img
                          src={scenario.burnMapUrl}
                          alt="Historical burn map overlay"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            opacity: burnMapOpacity,
                            pointerEvents: "none",
                            zIndex: viewMode === "2D" ? 7 : 1,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              <PlaybackBar
                status={simulationStatus}
                onStart={handleStart}
                onPause={handlePause}
                onReset={handleReset}
                speed={speed}
                onSpeedChange={setSpeed}
                currentSimTime={currentSimTime}
                startTime={scenario?.startTime}
                endTime={scenario?.endTime}
              />
            </Paper>
          </Grid>

          {/* ---------------- Sidebar column ---------------- */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <ConditionsCard weather={currentWeather} moisture={infraStats?.moisture} />
              <ImpactCard
                stats={infraStats}
                history={statsHistory}
                cellSizeM={scenario?.cellSizeM}
                totalStructures={scenario?.totalStructures}
              />
              <Paper sx={{ p: 2 }}>
                <Tabs
                  value={sidebarTab}
                  onChange={(_, v) => setSidebarTab(v)}
                  variant="fullWidth"
                  sx={{ mb: 1.5, minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
                >
                  <Tab label="Response" />
                  <Tab label="Forecast" />
                  <Tab label="Strategy Lab" />
                </Tabs>
                {sidebarTab === 0 && (
                  <ResponsePanel
                    droneModeActive={droneModeActive}
                    onDroneModeChange={handleDroneModeChange}
                    strategyId={droneStrategyId}
                    onStrategyChange={setDroneStrategyId}
                    droneDensity={droneDensity}
                    onDroneDensityChange={setDroneDensity}
                    stationCount={droneStations.length}
                    disabled={simulationStatus === "running"}
                  />
                )}
                {sidebarTab === 1 && (
                  <ForecastPanel
                    forecast={forecast}
                    running={forecastRunning}
                    progress={forecastProgress}
                    onRun={handleRunForecast}
                    horizon={forecastHorizon}
                    onHorizonChange={setForecastHorizon}
                    overlayVisible={forecastVisible}
                    onOverlayToggle={setForecastVisible}
                    disabled={!scenario || loading}
                  />
                )}
                {sidebarTab === 2 && (
                  <StrategyLab
                    scenario={scenario}
                    getEngine={() => engineRef.current}
                    getSimTimeMs={() => simTimeRef.current}
                    onApplyStrategy={handleApplyStrategy}
                    disabled={!scenario || loading}
                  />
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}

export default App;
